import { useCallback, useMemo, useState } from 'react';

import {
  flattenTags,
  mapTagsToCategories,
} from 'legacy/src/components/learnosity/utils/helpers';

// These are the categories whose tags get sent to Learnosity
const reportTagCategories = /(Unit|Topic|Skill Category|Skill|Subunit)/;

const isReportTagCategory = (category) =>
  category.description.search(reportTagCategories) > -1;

export default function useTags(question) {
  const [tags, setTags] = useState({});
  const [tagCategories, setTagCategories] = useState(null);
  const [allRequiredAreSelected, setAllRequiredAreSelected] = useState(null);
  const [isTagSelected, setIsTagSelected] = useState(false);

  const questionTags = useMemo(
    () => (question ? mapTagsToCategories(question.tags) : {}),
    [question],
  );

  const flattenedTags = useMemo(
    () =>
      tagCategories
        ? flattenTags(
            Array.prototype.concat(
              tagCategories.required_editable.filter(isReportTagCategory),
              tagCategories.additional_editable.filter(isReportTagCategory),
            ),
          )
        : null,
    [tagCategories],
  );

  const selectedTagsInfo = useMemo(() => {
    if (flattenedTags) {
      const selectedTagIds = Object.keys(tags).reduce(
        (curr, next) => [...curr, ...tags[next]],
        [],
      );

      return flattenedTags
        .filter((cat) => selectedTagIds.includes(cat.id))
        .map((cat) => ({
          type: cat.category_id
            ? flattenedTags.find((ftag) => ftag.id === cat.category_id)
                .description || null
            : null,
          name: cat.description,
        }));
    }
    return null;
  }, [tags, flattenedTags]);

  const updateTags = useCallback((selectedTags, categories) => {
    const requiredCategoryIds = categories.required_editable.map(
      (cat) => cat.id,
    );
    const isAllRequiredSelected = requiredCategoryIds.every(
      (categoryId) =>
        selectedTags[categoryId] && selectedTags[categoryId].length > 0,
    );

    const isRequiredTagSelected = Object.keys(selectedTags).some(
      (categoryId) => selectedTags[categoryId].length > 0,
    );

    if (!tagCategories) {
      setTagCategories(categories);
    }

    setAllRequiredAreSelected(isAllRequiredSelected);
    setIsTagSelected(isRequiredTagSelected);
    setTags(selectedTags);
  }, []);

  return {
    tags,
    questionTags,
    selectedTagsInfo,
    allRequiredAreSelected,
    isTagSelected,
    setTags,
    updateTags,
  };
}
